import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimerIcon from "@mui/icons-material/Timer";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import StreakCounter from "../../../components/StreakCounter";
import { formatDateForStorage } from "../../../components/GameStateUtil";
import {
  getPonderStreakFromStorage,
  getIsPonderStreakActive,
  updatePonderStreak,
} from "../components/PonderStreakUtil";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import CalendarArchive from "../components/CalendarArchive";
import { getSudoku } from "sudoku-gen";
import seedrandom from "seedrandom";

const GOOSE_SVG_PATH = "/assets/ducks/sudo-goose.svg";
const GRID_SIZE = 9;

// Convert sudoku-gen string format to 2D grid
function stringToGrid(sudokuString) {
  const grid = [];
  for (let i = 0; i < GRID_SIZE; i++) {
    grid[i] = [];
    for (let j = 0; j < GRID_SIZE; j++) {
      const char = sudokuString[i * GRID_SIZE + j];
      grid[i][j] = char === "-" ? 0 : parseInt(char, 10);
    }
  }
  return grid;
}

// Convert 2D grid to sudoku-gen string format
function gridToString(grid) {
  return grid
    .flat()
    .map((cell) => (cell === 0 ? "-" : cell.toString()))
    .join("");
}

// Generate a seeded Sudoku puzzle using sudoku-gen
function generateSeededSudoku(date) {
  // Create a deterministic seed from the date
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const dateString = `${y}${m}${d}`;

  // Use seedrandom to create a deterministic random number generator
  const rng = seedrandom(dateString);

  // Override Math.random temporarily to use our seeded generator
  const originalRandom = Math.random;
  Math.random = () => rng();

  try {
    // Generate the puzzle using sudoku-gen
    const sudoku = getSudoku("easy"); // You can adjust difficulty: 'easy', 'medium', 'hard', 'expert'

    // Convert to 2D grids
    const puzzleGrid = stringToGrid(sudoku.puzzle);
    const solutionGrid = stringToGrid(sudoku.solution);

    return { puzzle: puzzleGrid, solution: solutionGrid };
  } finally {
    // Restore original Math.random
    Math.random = originalRandom;
  }
}

const SudoGoose = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timer, setTimer] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [bestTime, setBestTime] = useState(null);
  const [streak, setStreak] = useState(0);
  const [isStreakActive, setIsStreakActive] = useState(false);
  const [showHowToModal, setShowHowToModal] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [puzzle, setPuzzle] = useState(null);
  const [solution, setSolution] = useState(null);
  const [userGrid, setUserGrid] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null); // [row, col]
  const [initialCells, setInitialCells] = useState(null); // true if cell is prefilled
  const [gameWon, setGameWon] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const theme = useTheme();
  const isMobile = true;

  // Timer effect
  useEffect(() => {
    let intervalId;
    if (isActive) {
      intervalId = setInterval(() => {
        setTimer((t) => t + 1);
      }, 1000);
    }
    return () => intervalId && clearInterval(intervalId);
  }, [isActive]);

  // Load streak on date change
  useEffect(() => {
    const { count } = getPonderStreakFromStorage("sudogoose");
    setStreak(count);
    setIsStreakActive(getIsPonderStreakActive("sudogoose"));
  }, [currentDate]);

  // Load best time for the current date
  useEffect(() => {
    const dateKey = formatDateForStorage(currentDate);
    const best = localStorage.getItem(`sudogoose_bestTime-${dateKey}`);
    setBestTime(best ? parseInt(best, 10) : null);
  }, [currentDate]);

  // Generate daily puzzle
  useEffect(() => {
    const { puzzle, solution } = generateSeededSudoku(currentDate);
    setPuzzle(puzzle);
    setSolution(solution);
  }, [currentDate]);

  // When puzzle is generated, initialize userGrid and initialCells
  useEffect(() => {
    if (puzzle) {
      setUserGrid(puzzle.map((row) => [...row]));
      setInitialCells(puzzle.map((row) => row.map((cell) => cell !== 0)));
      setSelectedCell(null);
    }
  }, [puzzle]);

  // Win detection
  useEffect(() => {
    if (!userGrid || !solution) return;
    const isSolved = userGrid.every((row, i) =>
      row.every((cell, j) => cell === solution[i][j])
    );
    if (isSolved && !gameWon) {
      setGameWon(true);
      setIsActive(false);
      setShowEndModal(true);
      // Save best time
      const dateKey = formatDateForStorage(currentDate);
      const best = localStorage.getItem(`sudogoose_bestTime-${dateKey}`);
      if (!best || timer < parseInt(best, 10)) {
        localStorage.setItem(`sudogoose_bestTime-${dateKey}`, timer.toString());
      }
      // Update streak
      const newStreak = updatePonderStreak(new Date(), "sudogoose");
      setStreak(newStreak);
      setIsStreakActive(true);
      // Update calendar/localStorage with completion data
      const gameData = {
        completed: true,
        time: timer,
        bestTime: best ? Math.min(timer, parseInt(best, 10)) : timer,
      };
      localStorage.setItem(`sudogoose-${dateKey}`, JSON.stringify(gameData));
    }
  }, [userGrid, solution, gameWon, timer, currentDate]);

  // Auto-win keyboard shortcut (Ctrl/Cmd+Shift+A)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "a"
      ) {
        e.preventDefault();
        if (solution) {
          setUserGrid(solution.map((row) => [...row]));
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [solution]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Toggle mute state
  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
        minHeight: "640px",
        width: "100%",
        px: { xs: 1, sm: 2 },
        pt: { xs: 1, sm: 2 },
        pb: { xs: 1, sm: 2 },
        mx: "auto",
        gap: { xs: 0.5, sm: 1 },
        position: "relative",
        backgroundColor: "#F5F0EB",
        borderRadius: 0,
        boxShadow: "none",
        overflow: "hidden",
      }}
    >
      {/* Game HUD */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "sm",
          p: 2,
          mb: 2,
          borderRadius: 2,
          bgcolor: "white",
          boxShadow: 2,
        }}
      >
        {/* Top row: Controls, Date, Streak */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          {/* Left: Help and Sound controls */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="How to Play">
              <IconButton
                onClick={() => setShowHowToModal(true)}
                sx={{
                  color: "#222",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isMuted ? "Unmute" : "Mute"}>
              <IconButton
                onClick={toggleMute}
                sx={{
                  color: "#222",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                }}
              >
                {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          {/* Center: Date */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.25rem",
              color: "#222",
              cursor: "pointer",
              textDecoration: "underline",
              transition: "text-decoration 0.2s",
              "&:hover": { textDecoration: "underline dotted" },
            }}
            onClick={() => setShowCalendar(true)}
          >
            {currentDate.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </Typography>
          {/* Right: Streak */}
          <StreakCounter isActive={isStreakActive} streak={streak} />
        </Box>
        {/* Bottom row: Timer and Best Time */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Chip
            icon={<TimerIcon sx={{ color: "#FFD700" }} />}
            label={`Time: ${formatTime(timer)}`}
            sx={{
              bgcolor: "rgba(0, 0, 0, 0.1)",
              "& .MuiChip-label": {
                fontWeight: 600,
                color: "#000000",
              },
            }}
          />
          <Chip
            icon={<EmojiEventsIcon sx={{ color: "#FFD700" }} />}
            label={`Best: ${bestTime !== null ? formatTime(bestTime) : "---"}`}
            sx={{
              bgcolor: "rgba(0, 0, 0, 0.1)",
              "& .MuiChip-label": {
                fontWeight: 600,
                color: "#222",
              },
            }}
          />
        </Box>
      </Box>
      {/* Sudoku Grid */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "sm",
          aspectRatio: "1/1",
          bgcolor: "#fff",
          border: "2.5px solid black",
          display: "grid",
          gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
          gridTemplateRows: `repeat(${GRID_SIZE}, 1fr)`,
          gap: 0,
          mb: 2,
          touchAction: "manipulation",
          userSelect: "none",
        }}
      >
        {userGrid &&
          (() => {
            // Get the selected number
            const selectedNumber =
              selectedCell && userGrid
                ? userGrid[selectedCell[0]][selectedCell[1]]
                : null;
            return userGrid.map((row, i) =>
              row.map((cell, j) => {
                const thickLeft = j % 3 === 0 && j !== 0;
                const thickTop = i % 3 === 0 && i !== 0;
                const isSelected =
                  selectedCell &&
                  selectedCell[0] === i &&
                  selectedCell[1] === j;
                const isPrefilled = initialCells && initialCells[i][j];
                // Highlight if same number as selected (not empty, not the selected cell itself)
                const isSameNumber =
                  selectedNumber &&
                  cell === selectedNumber &&
                  cell !== 0 &&
                  !(
                    selectedCell &&
                    selectedCell[0] === i &&
                    selectedCell[1] === j
                  );
                return (
                  <Box
                    key={`${i}-${j}`}
                    onClick={() => {
                      if (!isPrefilled) setSelectedCell([i, j]);
                    }}
                    tabIndex={isPrefilled ? -1 : 0}
                    aria-label={
                      isPrefilled
                        ? cell === 9
                          ? "Goose (locked)"
                          : cell
                        : cell === 0
                        ? "Empty cell"
                        : cell === 9
                        ? "Goose"
                        : cell
                    }
                    sx={{
                      borderRight:
                        j === GRID_SIZE - 1 ? "none" : "1px solid black",
                      borderBottom:
                        i === GRID_SIZE - 1 ? "none" : "1px solid black",
                      borderLeft: thickLeft ? "2.5px solid black" : undefined,
                      borderTop: thickTop ? "2.5px solid black" : undefined,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: { xs: 20, sm: 28 },
                      fontWeight: 600,
                      color:
                        cell === 9
                          ? "#ECB061"
                          : isPrefilled
                          ? "#222"
                          : "#1976d2",
                      backgroundColor: isSelected
                        ? "#b8c26c33"
                        : isSameNumber
                        ? "#ffe082" // highlight color for matching numbers
                        : isPrefilled
                        ? "#f9f9f9"
                        : "#fff",
                      position: "relative",
                      userSelect: "none",
                      aspectRatio: "1/1",
                      boxShadow: isSelected
                        ? "0 0 0 3px #ECB061 inset"
                        : "none",
                      outline: "none",
                      cursor: isPrefilled ? "default" : "pointer",
                      transition: "background 0.15s, outline 0.15s",
                      minWidth: 0,
                      minHeight: 0,
                      touchAction: "manipulation",
                    }}
                  >
                    {cell === 0 ? (
                      ""
                    ) : cell === 9 ? (
                      <img
                        src={GOOSE_SVG_PATH}
                        alt="Goose"
                        style={{ width: 28, height: 28, pointerEvents: "none" }}
                      />
                    ) : (
                      cell
                    )}
                  </Box>
                );
              })
            );
          })()}
      </Box>
      {/* Number Pad */}
      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            maxWidth: "sm",
            width: "100%",
            flexShrink: 0,
            mt: { xs: 2, sm: 3 },
            mb: { xs: 0.5, sm: 1 },
            pt: 0,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: "100%",
              justifyContent: "center",
            }}
          >
            {[1, 2, 3, 4, 5].map((num) => (
              <Box
                key={num}
                component="button"
                onClick={() => {
                  if (!selectedCell || !userGrid || !initialCells) return;
                  const [i, j] = selectedCell;
                  if (initialCells[i][j]) return;
                  setUserGrid((prev) => {
                    const next = prev.map((row) => [...row]);
                    next[i][j] = num;
                    return next;
                  });
                  if (!isActive) setIsActive(true);
                }}
                aria-label={num === 9 ? "Goose" : num}
                sx={{
                  flex: 1,
                  width: { xs: 0, sm: 0 },
                  minWidth: { xs: 48, sm: 60 },
                  height: { xs: 48, sm: 60 },
                  minHeight: { xs: 48, sm: 60 },
                  p: 0,
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  border: "2px solid #5780bd",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", sm: "1.8rem" },
                  color: num === 9 ? "#ECB061" : "#222",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  outline: "none",
                  "&:active": {
                    backgroundColor: "#b8c26c22",
                    transform: "scale(0.96)",
                  },
                  "&:focus": {
                    border: "2.5px solid #ECB061",
                  },
                  mx: 0.5,
                  userSelect: "none",
                  touchAction: "manipulation",
                }}
              >
                {num === 9 ? (
                  <img
                    src={GOOSE_SVG_PATH}
                    alt="Goose"
                    style={{ width: 36, height: 36, pointerEvents: "none" }}
                  />
                ) : (
                  num
                )}
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: "100%",
              justifyContent: "center",
            }}
          >
            {[6, 7, 8, 9].map((num) => (
              <Box
                key={num}
                component="button"
                onClick={() => {
                  if (!selectedCell || !userGrid || !initialCells) return;
                  const [i, j] = selectedCell;
                  if (initialCells[i][j]) return;
                  setUserGrid((prev) => {
                    const next = prev.map((row) => [...row]);
                    next[i][j] = num;
                    return next;
                  });
                  if (!isActive) setIsActive(true);
                }}
                aria-label={num === 9 ? "Goose" : num}
                sx={{
                  flex: 1,
                  width: { xs: 0, sm: 0 },
                  minWidth: { xs: 48, sm: 60 },
                  height: { xs: 48, sm: 60 },
                  minHeight: { xs: 48, sm: 60 },
                  p: 0,
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  border: "2px solid #5780bd",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", sm: "1.8rem" },
                  color: num === 9 ? "#ECB061" : "#222",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  outline: "none",
                  "&:active": {
                    backgroundColor: "#b8c26c22",
                    transform: "scale(0.96)",
                  },
                  "&:focus": {
                    border: "2.5px solid #ECB061",
                  },
                  mx: 0.5,
                  userSelect: "none",
                  touchAction: "manipulation",
                }}
              >
                {num === 9 ? (
                  <img
                    src={GOOSE_SVG_PATH}
                    alt="Goose"
                    style={{ width: 36, height: 36, pointerEvents: "none" }}
                  />
                ) : (
                  num
                )}
              </Box>
            ))}
            {/* Trash button */}
            <Box
              component="button"
              onClick={() => {
                if (!selectedCell || !userGrid || !initialCells) return;
                const [i, j] = selectedCell;
                if (initialCells[i][j]) return;
                setUserGrid((prev) => {
                  const next = prev.map((row) => [...row]);
                  next[i][j] = 0;
                  return next;
                });
                if (!isActive) setIsActive(true);
              }}
              aria-label="Clear cell"
              sx={{
                flex: 1,
                width: { xs: 0, sm: 0 },
                minWidth: { xs: 48, sm: 60 },
                height: { xs: 48, sm: 60 },
                minHeight: { xs: 48, sm: 60 },
                p: 0,
                borderRadius: "10px",
                backgroundColor: "#fff",
                border: "2px solid #e57373",
                boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s",
                outline: "none",
                "&:active": {
                  backgroundColor: "#e5737322",
                  transform: "scale(0.96)",
                },
                "&:focus": {
                  border: "2.5px solid #ECB061",
                },
                mx: 0.5,
                userSelect: "none",
                touchAction: "manipulation",
              }}
            >
              <DeleteOutlineIcon
                sx={{ fontSize: { xs: 28, sm: 32 }, color: "#e57373" }}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            flexWrap: "nowrap",
            maxWidth: 400,
            width: "100%",
            flexShrink: 0,
            mt: { xs: 2, sm: 3 },
            mb: { xs: 0.5, sm: 1 },
            pt: 0,
            position: "relative",
          }}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <Box
              key={num}
              component="button"
              onClick={() => {
                if (!selectedCell || !userGrid || !initialCells) return;
                const [i, j] = selectedCell;
                if (initialCells[i][j]) return;
                setUserGrid((prev) => {
                  const next = prev.map((row) => [...row]);
                  next[i][j] = num;
                  return next;
                });
                if (!isActive) setIsActive(true);
              }}
              aria-label={num === 9 ? "Goose" : num}
              sx={{
                flex: 1,
                width: { xs: 0, sm: 0 },
                minWidth: { xs: 52, sm: 64 },
                height: { xs: 52, sm: 64 },
                minHeight: { xs: 52, sm: 64 },
                p: 0,
                borderRadius: "10px",
                backgroundColor: "#fff",
                border: "2px solid #5780bd",
                boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: { xs: "1.7rem", sm: "2rem" },
                color: num === 9 ? "#ECB061" : "#222",
                cursor: "pointer",
                transition: "all 0.15s",
                outline: "none",
                "&:active": {
                  backgroundColor: "#b8c26c22",
                  transform: "scale(0.96)",
                },
                "&:focus": {
                  border: "2.5px solid #ECB061",
                },
                mx: 0.5,
                userSelect: "none",
                touchAction: "manipulation",
              }}
            >
              {num === 9 ? (
                <img
                  src={GOOSE_SVG_PATH}
                  alt="Goose"
                  style={{ width: 40, height: 40, pointerEvents: "none" }}
                />
              ) : (
                num
              )}
            </Box>
          ))}
          {/* Trash button */}
          <Box
            component="button"
            onClick={() => {
              if (!selectedCell || !userGrid || !initialCells) return;
              const [i, j] = selectedCell;
              if (initialCells[i][j]) return;
              setUserGrid((prev) => {
                const next = prev.map((row) => [...row]);
                next[i][j] = 0;
                return next;
              });
              if (!isActive) setIsActive(true);
            }}
            aria-label="Clear cell"
            sx={{
              flex: 1,
              width: { xs: 0, sm: 0 },
              minWidth: { xs: 52, sm: 64 },
              height: { xs: 52, sm: 64 },
              minHeight: { xs: 52, sm: 64 },
              p: 0,
              borderRadius: "10px",
              backgroundColor: "#fff",
              border: "2px solid #e57373",
              boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.15s",
              outline: "none",
              "&:active": {
                backgroundColor: "#e5737322",
                transform: "scale(0.96)",
              },
              "&:focus": {
                border: "2.5px solid #ECB061",
              },
              mx: 0.5,
              userSelect: "none",
              touchAction: "manipulation",
            }}
          >
            <DeleteOutlineIcon
              sx={{ fontSize: { xs: 32, sm: 36 }, color: "#e57373" }}
            />
          </Box>
        </Box>
      )}
      {/* End Game Modal */}
      <Dialog open={showEndModal} onClose={() => setShowEndModal(false)}>
        <DialogContent sx={{ textAlign: "center", p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            🎉 You solved it!
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Time: {formatTime(timer)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Streak: {streak}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowEndModal(false)}
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
      {/* Calendar Archive Modal */}
      <CalendarArchive
        open={showCalendar}
        onClose={() => setShowCalendar(false)}
        gameKey="sudogoose"
        getGameDataForDate={(dateKey) => {
          const data = localStorage.getItem(`sudogoose-${dateKey}`);
          if (!data) return null;
          const gameData = JSON.parse(data);
          return {
            completed: gameData.completed,
            time: gameData.time,
            best: gameData.bestTime ? `${gameData.bestTime} sec` : null,
          };
        }}
        formatDateForStorage={formatDateForStorage}
        title="SudoGoose Archive"
        description="View your past SudoGoose games and achievements"
        onDateSelect={(date) => {
          setCurrentDate(date);
          setShowCalendar(false);
        }}
        currentDate={currentDate}
        showGoal={false}
      />
      {/* HowToPlayModal placeholder */}
      {/* TODO: Add HowToPlayModal when ready */}
    </Box>
  );
};

export default SudoGoose;
